import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getThreadCmItems, addPreviewLine, updatePreviewCredit, deletePreviewLine,
  getDiscountItems, getCustomerInvoices, getSoPreviewBundle,
  getCmPreviewPrefs, saveCmPreviewPrefs, showToast,
  CustomerInvoice, CmHeaderFields, SoPreviewBundle, SoOption,
} from '../services/api';
import { MessageItem } from '../types';

// ── Credit Memo (CM) preview ──────────────────────────────────────────────────
// The credit-desk counterpart of the SO/RMA preview (OrderItemEditor). Kelsey (the
// credit manager) reviews a CREDIT thread, ties in an existing invoice (reference-
// only), adds one or more DIS* "make goods"/discount credit lines at a value, and
// creates a STANDALONE NetSuite Credit Memo (number like CM26068). It is a SEPARATE
// component — not a 4th mode on OrderItemEditor — because the CM has none of the
// order machinery (qty/unit/weight/pallet conversion/drop-ship) and mixing it in
// would risk regressing the live SO/RMA create paths.
//
// Parity with the real NetSuite CM form (fields Kelsey fills): customer (locked to
// the thread), date, location, sales rep, department/class, header discount + rate,
// customer PO#, the DIS-item credit line(s) + amount, the tied-in invoice, and the
// memo (the credit reason). Everything else on the NetSuite CM form defaults exactly
// as it does in the UI. HARD guard: no customer linked -> no create (mirrors the
// server-side 400).

// One editable CM credit line (a persisted message_items row, line_kind='CM').
interface CmLine {
  id: number;                         // message_items.id (always persisted)
  item_id: number | null;             // the DIS* item
  resolved_item: string | null;       // its display name
  credit_amount: number | null;       // the credit VALUE (positive)
  cm_group?: number | null;           // which CM preview TAB this line belongs to (NULL=1)
  department_id: string | null;       // per-line NetSuite department internal id (the DEPARTMENT column)
}

interface CreditMemoEditorProps {
  // The credit message id (thread anchor) — the CM lines + header prefs key on it.
  messageId: number;
  customerId?: number | null;
  customerName?: string | null;
  // Bumps when the thread updates so the preview refetches its persisted lines.
  refreshKey?: string | number | null;
  // Owned by the parent (ConversationCard), mirroring the SO/RMA status flow.
  cmStatus: 'idle' | 'loading' | 'success' | 'error';
  cmResult: string;
  onCreateCM: (items: MessageItem[], header: CmHeaderFields, memo: string) => void;
  // Multi-preview: which CM tab (1,2,...) this instance renders. Its lines + header
  // prefs are scoped to this group. Undefined => group 1 (single-preview default).
  group?: number;
}

// A typeable / searchable DEPARTMENT picker for a CM credit line — the SLX
// counterpart of the DEPARTMENT column on a NetSuite Credit Memo line. A native
// <select> can't type-to-search, and the department list can be long, so this is a
// filterable combobox: click to open, type to narrow, click an option to pick. The
// value is the NetSuite department internal id; '' selects "none". Self-contained
// (no extra deps) so it doesn't touch the live SO/RMA create paths.
const DepartmentCombo: React.FC<{
  value: string | null;
  options: SoOption[];
  onChange: (id: string) => void;
  disabled?: boolean;
}> = ({ value, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => String(o.id) === String(value ?? ''));
  const selectedName = selected ? selected.name : '';

  // Close when clicking outside the combobox.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false); setQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter(o => (o.name || '').toLowerCase().includes(q)) : options;
  const pick = (id: string) => { onChange(id); setOpen(false); setQuery(''); };

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        value={open ? query : selectedName}
        disabled={disabled}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
        placeholder="— department —"
        className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
        title="Line department (NetSuite) — type to search"
      />
      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded border border-gray-200 bg-white shadow-lg text-xs">
          <button type="button"
            onMouseDown={e => { e.preventDefault(); pick(''); }}
            className="block w-full text-left px-2 py-1 text-gray-400 hover:bg-purple-50">
            — none —
          </button>
          {filtered.length === 0 && <div className="px-2 py-1 text-gray-400">No match</div>}
          {filtered.map(o => (
            <button key={o.id} type="button"
              onMouseDown={e => { e.preventDefault(); pick(String(o.id)); }}
              className={`block w-full text-left px-2 py-1 hover:bg-purple-50 ${
                String(o.id) === String(value ?? '') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}>
              {o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CreditMemoEditor: React.FC<CreditMemoEditorProps> = ({
  messageId, customerId, customerName, refreshKey, cmStatus, cmResult, onCreateCM,
  group = 1,
}) => {
  const [lines, setLines] = useState<CmLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState<CmHeaderFields>({});
  const [memo, setMemo] = useState('');
  const [discountItems, setDiscountItems] = useState<{ id: string; itemid: string; displayname: string }[]>([]);
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [bundle, setBundle] = useState<SoPreviewBundle | null>(null);

  // prefs load gate + debounced autosave (mirrors OrderItemEditor's prefs pattern):
  // never autosave before the persisted header has loaded, and only after a change.
  const prefsLoadedRef = useRef(false);
  const dirtyRef = useRef(false);
  // NON-BLANKING refetch (mirrors OrderItemEditor): show the "Loading" placeholder
  // ONLY on the very FIRST load. The refreshKey includes the ~2s open-tick, so a
  // background refetch fires every poll; blanking to a spinner each time made the CM
  // preview "pop in and out". After the first load we keep the form on screen and
  // quietly swap in the fresh lines. Resets on remount (messageId/group change).
  const hasLoadedRef = useRef(false);
  // Per-line "amount being typed" guard (D1): a background poll refetch must NOT
  // overwrite a credit amount the rep is mid-typing. Added on change, cleared
  // once the blur save persists.
  const dirtyLinesRef = useRef<Set<number>>(new Set());
  // Latest header/memo for the unmount flush (D1): a debounced autosave still
  // pending when the modal closes must be written, not cancelled.
  const headerLatestRef = useRef(header);
  const memoLatestRef = useRef(memo);
  headerLatestRef.current = header;
  memoLatestRef.current = memo;

  // ── load persisted CM lines for the thread ─────────────────────────────────
  const fetchLines = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      // Server-filtered to this tab's group; client filter as a safety net.
      const rows = await getThreadCmItems(messageId, group);
      setLines(prevLocal => {
        const localById = new Map(prevLocal.map(l => [l.id, l]));
        return (Array.isArray(rows) ? rows : [])
          .filter(r => ((r.preview_group ?? 1) === group))
          .map(r => {
            const id = Number(r.id);
            const base = {
              id,
              item_id: r.item_id ?? null,
              resolved_item: r.resolved_item ?? null,
              credit_amount: r.credit_amount ?? null,
              cm_group: r.preview_group ?? 1,
              department_id: ((r as any).line_department_id != null
                ? String((r as any).line_department_id) : null),
            };
            // D1: don't clobber a credit amount the rep is mid-typing — keep the
            // local value for a line whose amount hasn't been saved (blurred) yet.
            const local = localById.get(id);
            if (dirtyLinesRef.current.has(id) && local) {
              return { ...base, credit_amount: local.credit_amount };
            }
            return base;
          });
      });
      hasLoadedRef.current = true;
    } catch (e) {
      console.error('CM lines load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [messageId, group]);

  useEffect(() => { fetchLines(); }, [fetchLines, refreshKey]);

  // ── option lists: DIS* items, the customer's invoices, header dropdowns ─────
  useEffect(() => {
    getDiscountItems().then(setDiscountItems).catch(() => setDiscountItems([]));
  }, []);

  useEffect(() => {
    if (!customerId) { setInvoices([]); return; }
    getCustomerInvoices(customerId).then(setInvoices).catch(() => setInvoices([]));
  }, [customerId]);

  useEffect(() => {
    getSoPreviewBundle(customerId).then(setBundle).catch(() => setBundle(null));
  }, [customerId]);

  // ── load persisted header (prefill sales rep from the customer default) ─────
  useEffect(() => {
    if (!messageId) return;
    let cancelled = false;
    getCmPreviewPrefs(messageId, group)
      .then(prefs => {
        if (cancelled) return;
        const h: CmHeaderFields = (prefs && prefs.header) || {};
        setHeader(prev => ({ ...prev, ...h }));
        if (typeof (prefs && prefs.memo) === 'string') setMemo(prefs.memo);
        prefsLoadedRef.current = true;
      })
      .catch(e => { console.error('CM prefs load failed:', e); prefsLoadedRef.current = true; });
    return () => { cancelled = true; };
  }, [messageId, group]);

  // Seed the sales-rep default from the bundle once (only if the rep isn't set yet).
  useEffect(() => {
    if (!bundle || !prefsLoadedRef.current) return;
    setHeader(prev => (prev.sales_rep_id ? prev
      : { ...prev, sales_rep_id: bundle.defaults?.salesrep_id ?? prev.sales_rep_id }));
  }, [bundle]);

  // ── debounced autosave of header + memo ─────────────────────────────────────
  useEffect(() => {
    if (!prefsLoadedRef.current || !dirtyRef.current || !messageId) return;
    const t = setTimeout(() => {
      saveCmPreviewPrefs(messageId, { header, memo }, group).catch(e =>
        console.error('CM prefs save failed:', e));
    }, 600);
    return () => clearTimeout(t);
  }, [header, memo, messageId, group]);

  // D1: UNMOUNT FLUSH — if the modal closes within the 600ms debounce window, the
  // effect above only clears the timer, so the last header/memo edit would be
  // lost. Fire-and-forget the latest values on unmount (only if a change is
  // pending and prefs finished loading). Mount-only so it runs exactly once.
  useEffect(() => () => {
    if (prefsLoadedRef.current && dirtyRef.current && messageId) {
      saveCmPreviewPrefs(messageId,
        { header: headerLatestRef.current, memo: memoLatestRef.current },
        group).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchHeader = (patch: Partial<CmHeaderFields>) => {
    dirtyRef.current = true;
    setHeader(prev => ({ ...prev, ...patch }));
  };
  const changeMemo = (v: string) => { dirtyRef.current = true; setMemo(v); };

  // ── line ops (all persisted; the AI grounding never touches CM rows) ─────────
  const addLine = async () => {
    try {
      const res = await addPreviewLine(messageId, { line_kind: 'CM', preview_group: group });
      const newId = res && res.id;
      if (newId) setLines(prev => [...prev, { id: Number(newId), item_id: null, resolved_item: null, credit_amount: null, cm_group: group, department_id: null }]);
    } catch (e) {
      console.error('CM add line failed:', e);
      showToast('Could not add a line — please try again.');
    }
  };

  const changeLineItem = async (lineId: number, itemIdStr: string) => {
    const opt = discountItems.find(d => d.id === itemIdStr);
    const item_id = itemIdStr ? Number(itemIdStr) : null;
    const resolved_item = opt ? (opt.displayname || opt.itemid) : null;
    const snapshot = lines;   // for revert if the save fails
    setLines(prev => prev.map(l => l.id === lineId ? { ...l, item_id, resolved_item } : l));
    try {
      await updatePreviewCredit(messageId, { line_id: lineId, item_id, resolved_item });
    } catch (e) {
      console.error('CM item change failed:', e);
      setLines(snapshot);   // D4: revert so the UI doesn't lie
      showToast('Could not save the item change — please try again.');
    }
  };

  // Per-line DEPARTMENT pick (the DEPARTMENT column). deptId '' clears it (-> NULL).
  const changeLineDepartment = async (lineId: number, deptId: string) => {
    const snapshot = lines;
    setLines(prev => prev.map(l => l.id === lineId ? { ...l, department_id: deptId || null } : l));
    try {
      await updatePreviewCredit(messageId, { line_id: lineId, department_id: deptId });
    } catch (e) {
      console.error('CM department change failed:', e);
      setLines(snapshot);
      showToast('Could not save the department — please try again.');
    }
  };

  const changeLineAmount = (lineId: number, v: string) => {
    const amt = v === '' ? null : Number(v);
    dirtyLinesRef.current.add(lineId);   // D1: shield from the poll until blur saves
    setLines(prev => prev.map(l => l.id === lineId ? { ...l, credit_amount: amt } : l));
  };
  const blurLineAmount = async (lineId: number) => {
    const line = lines.find(l => l.id === lineId);
    if (!line) return;
    try {
      await updatePreviewCredit(messageId, { line_id: lineId, credit_amount: line.credit_amount });
      dirtyLinesRef.current.delete(lineId);   // saved — the poll may refresh it now
    } catch (e) {
      console.error('CM amount save failed:', e);
      // Keep the line dirty + the typed value so the rep can retry without losing input.
      showToast('Could not save the credit amount — please try again.');
    }
  };

  const removeLine = async (lineId: number) => {
    const snapshot = lines;
    setLines(prev => prev.filter(l => l.id !== lineId));
    try { await deletePreviewLine(messageId, lineId); }
    catch (e) {
      console.error('CM remove line failed:', e);
      setLines(snapshot);
      showToast('Could not remove the line — please try again.');
    }
  };

  // Tie in an invoice (reference-only): record its id + number in the header.
  const selectInvoice = (idStr: string) => {
    if (!idStr) { patchHeader({ invoice_id: null, invoice_tranid: null }); return; }
    const inv = invoices.find(i => String(i.id) === idStr);
    patchHeader({ invoice_id: inv ? Number(inv.id) : null, invoice_tranid: inv ? inv.tranid : null });
  };

  // Pull the tied-in invoice's total in as the credit value. Applies to the first
  // CM line (adds one if none yet), matching "credit the whole invoice amount".
  const pullInvoiceTotal = async () => {
    const inv = invoices.find(i => Number(i.id) === Number(header.invoice_id));
    if (!inv || inv.total == null) return;
    const amt = Math.abs(Number(inv.total) || 0);
    try {
      let targetId: number | undefined = lines.length ? lines[0].id : undefined;
      if (targetId == null) {
        const res = await addPreviewLine(messageId, { line_kind: 'CM', preview_group: group });
        targetId = res && res.id ? Number(res.id) : undefined;
        if (targetId == null) return;
        const newId = targetId;
        setLines(prev => [...prev, { id: newId, item_id: null, resolved_item: null, credit_amount: amt, cm_group: group, department_id: null }]);
      } else {
        const id = targetId;
        setLines(prev => prev.map(l => l.id === id ? { ...l, credit_amount: amt } : l));
      }
      const finalId = targetId;
      if (finalId == null) return;
      await updatePreviewCredit(messageId, { line_id: finalId, credit_amount: amt });
    } catch (e) {
      console.error('CM pull-total failed:', e);
    }
  };

  // A line is submit-ready when it has BOTH a DIS item and a positive amount.
  const resolvedLines = lines.filter(l => l.item_id != null && Number(l.credit_amount) > 0);
  const total = resolvedLines.reduce((s, l) => s + (Number(l.credit_amount) || 0), 0);
  // Department options for the per-line DEPARTMENT combobox (from the SO/CM bundle).
  const departments = (bundle?.departments || []) as SoOption[];
  const canCreate = !!customerId && resolvedLines.length > 0 && cmStatus === 'idle';

  const handleCreate = () => {
    // Pass MessageItem-shaped rows (createCreditMemo reads item_id + credit_amount).
    const items = resolvedLines.map(l => ({
      id: l.id, item_id: l.item_id, resolved_item: l.resolved_item,
      credit_amount: l.credit_amount, line_kind: 'CM',
      // Per-line department -> the NetSuite CM line's DEPARTMENT (createCreditMemo).
      line_department_id: l.department_id,
    })) as unknown as MessageItem[];
    onCreateCM(items, header, memo);
  };

  const selClass = 'text-xs border border-gray-300 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500';
  const invLabel = (i: CustomerInvoice) =>
    `${i.tranid}${i.total != null ? ` — $${Number(i.total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}`;

  if (loading) return <div className="p-3 text-sm text-gray-500">Loading credit memo...</div>;

  return (
    <div className="mt-3 p-3 rounded-lg bg-purple-50 border border-purple-200">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700">Credit Memo</h4>
        <span className="text-xs text-purple-600 font-medium">
          {customerName || (customerId ? `Customer ${customerId}` : 'No customer')}
        </span>
      </div>

      {/* HARD customer guard — Kelsey's requirement (also enforced server-side). */}
      {!customerId && (
        <div className="mb-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          No customer is linked to this thread. Assign a customer before creating a credit memo.
        </div>
      )}

      {/* ── Header (parity fields Kelsey fills) ──────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <label className="flex flex-col text-xs text-gray-500">
          Date
          <input type="date" value={header.date ?? ''} className={selClass}
            onChange={e => patchHeader({ date: e.target.value || null })} />
        </label>
        <label className="flex flex-col text-xs text-gray-500">
          Location
          <select className={selClass} value={header.location_id ?? ''}
            onChange={e => patchHeader({ location_id: e.target.value || null })}>
            <option value="">— select —</option>
            {(bundle?.locations || []).map((o: SoOption) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-gray-500">
          Sales rep
          <select className={selClass} value={header.sales_rep_id ?? ''}
            onChange={e => patchHeader({ sales_rep_id: e.target.value || null })}>
            <option value="">— select —</option>
            {(bundle?.salesReps || []).map((o: SoOption) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-gray-500">
          PO #
          <input type="text" value={header.po_number ?? ''} className={selClass}
            placeholder="customer PO"
            onChange={e => patchHeader({ po_number: e.target.value || null })} />
        </label>
      </div>

      {/* ── Tie in an invoice (reference-only) ───────────────────────────────── */}
      <div className="flex items-end gap-2 mb-3">
        <label className="flex flex-col text-xs text-gray-500 flex-1">
          Invoice (reference)
          <select className={selClass} value={header.invoice_id != null ? String(header.invoice_id) : ''}
            onChange={e => selectInvoice(e.target.value)} disabled={!customerId}>
            <option value="">— tie in an invoice —</option>
            {invoices.map(i => (
              <option key={i.id} value={String(i.id)}>{invLabel(i)}</option>
            ))}
          </select>
        </label>
        <button
          onClick={pullInvoiceTotal}
          disabled={header.invoice_id == null}
          className={`text-xs px-2.5 py-1.5 rounded-md ${header.invoice_id != null
            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
          title="Set the credit amount to the referenced invoice's total"
        >
          Use invoice total
        </button>
      </div>

      {/* ── Credit line(s): DIS* item + amount ───────────────────────────────── */}
      <div className="space-y-2 mb-2">
        {lines.map(line => (
          <div key={line.id} className="p-2 rounded border text-sm bg-white border-purple-200">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
              <span className="flex-shrink-0 w-16 text-xs font-semibold uppercase tracking-wide text-purple-600">Credit</span>
              <div className="flex-1 min-w-[12rem]">
                <select
                  value={line.item_id != null ? String(line.item_id) : ''}
                  onChange={e => changeLineItem(line.id, e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  title="Credit item (NetSuite DIS*)"
                >
                  <option value="">&mdash; select credit item &mdash;</option>
                  {line.item_id != null && !discountItems.some(d => d.id === String(line.item_id)) && (
                    <option value={String(line.item_id)}>{line.resolved_item || String(line.item_id)}</option>
                  )}
                  {discountItems.map(d => (
                    <option key={d.id} value={d.id}>{d.displayname || d.itemid}</option>
                  ))}
                </select>
              </div>
              <div className="flex-shrink-0 w-28 relative">
                <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">$</span>
                <input
                  type="number" min="0" step="0.01"
                  value={line.credit_amount ?? ''}
                  onChange={e => changeLineAmount(line.id, e.target.value)}
                  onBlur={() => blurLineAmount(line.id)}
                  placeholder="amount"
                  className="w-full text-sm text-right border border-gray-300 rounded pl-4 pr-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  title="Credit amount"
                />
              </div>
              <button
                onClick={() => removeLine(line.id)}
                className="flex-shrink-0 text-xs px-2 py-1 rounded text-red-500 hover:bg-red-50 ml-auto"
                title="Remove line"
              >✕</button>
            </div>
            {/* Per-line DEPARTMENT (typeable/searchable) — the CM line's DEPARTMENT
                column in NetSuite. */}
            <div className="mt-2 flex items-center gap-2">
              <span className="flex-shrink-0 w-16 text-xs font-medium text-gray-400">Dept</span>
              <div className="flex-1 min-w-[12rem]">
                <DepartmentCombo
                  value={line.department_id}
                  options={departments}
                  onChange={id => changeLineDepartment(line.id, id)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button onClick={addLine}
        className="text-xs text-purple-700 hover:text-purple-900 mb-3">+ Add credit line</button>

      {/* ── Memo (the credit reason, e.g. "200 BAGS 14-4 W/MITES $1/BAG") ─────── */}
      <label className="flex flex-col text-xs text-gray-500 mb-3">
        Memo (reason)
        <textarea value={memo} rows={2} className={`${selClass} resize-y`}
          placeholder="Reason for the credit"
          onChange={e => changeMemo(e.target.value)} />
      </label>

      {/* ── Create / status ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        {cmStatus === 'idle' && (
          <button
            onClick={handleCreate}
            disabled={!canCreate}
            className={`text-sm text-white px-4 py-2 rounded-md flex items-center ${canCreate
              ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-400 cursor-not-allowed'}`}
            title={!customerId ? 'Assign a customer first' : resolvedLines.length === 0 ? 'Add a credit line with an item and amount' : 'Create the credit memo in NetSuite'}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Create credit memo{total > 0 ? ` ($${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ''}
          </button>
        )}
        {cmStatus === 'loading' && <span className="text-sm text-gray-500">Creating credit memo…</span>}
        {cmStatus === 'success' && <span className="text-sm text-green-700">{cmResult || 'Credit memo created.'}</span>}
        {cmStatus === 'error' && <span className="text-sm text-red-600">{cmResult || 'Could not create credit memo.'}</span>}
      </div>
    </div>
  );
};

export default CreditMemoEditor;
